/**
 * BenchmarkPanel — beta, alpha and capture against the Nifty 50.
 *
 * This is the panel that answers "real edge, or riding market beta". Over a
 * 2020-2025 Indian bull market almost any long-only equity strategy shows a
 * handsome total return, so the return alone says nothing; beta and a
 * significance-tested alpha do.
 *
 * Props
 *   metrics  analytics.benchmark_metrics — may be the bare {total_return_pct}
 *            fallback when the curves share too few real trading days
 *   portfolioReturnPct  the strategy's own total return, for side-by-side
 */

import MetricInfo from './MetricInfo';

function fmtNum(v, d = 2) { return v != null ? v.toFixed(d) : '—'; }
function fmtPct(v) { return v != null ? `${v > 0 ? '+' : ''}${v.toFixed(2)}%` : '—'; }

function Stat({ label, value, sub, tone, info }) {
  const color = tone === 'good' ? 'text-win' : tone === 'bad' ? 'text-loss' : 'text-foreground';
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase font-semibold text-muted-foreground flex items-center gap-1">
        {label}{info && <MetricInfo metric={info} />}
      </span>
      <span className={`text-sm font-semibold ${color}`}>{value}</span>
      {sub && <span className="text-[10px] text-muted-foreground">{sub}</span>}
    </div>
  );
}

export default function BenchmarkPanel({ metrics, portfolioReturnPct }) {
  if (!metrics) return null;

  const hasRegression = metrics.beta != null;
  const t = metrics.alpha_t_stat;

  return (
    <div className="flex flex-col gap-3 bg-card shadow-lg p-4 border border-border rounded-lg">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-foreground">vs Nifty 50</h3>
        <span className="text-[10px] text-muted-foreground">
          {metrics.n_paired_days != null ? `${metrics.n_paired_days} paired days` : ''}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat
          label="Strategy"
          info="total_return_pct"
          value={fmtPct(portfolioReturnPct)}
          tone={portfolioReturnPct >= 0 ? 'good' : 'bad'}
        />
        <Stat label="Nifty 50" value={fmtPct(metrics.total_return_pct)} />
        {hasRegression && <>
          <Stat
            label="Beta"
            info="beta"
            value={fmtNum(metrics.beta)}
            sub={metrics.beta_deployed != null ? `${fmtNum(metrics.beta_deployed)} deployed` : null}
          />
          <Stat
            label="Alpha (ann.)"
            info="alpha_annual_pct"
            value={fmtPct(metrics.alpha_annual_pct)}
            /* The t-stat is the number that matters. Alpha without it is not
               actionable — in a single bull market a large, insignificant
               alpha is the common case. */
            sub={t != null ? `t = ${fmtNum(t)}` : 'significance n/a'}
            tone={metrics.alpha_significant === true
              ? (metrics.alpha_annual_pct >= 0 ? 'good' : 'bad')
              : undefined}
          />
          <Stat label="R²" info="r_squared" value={fmtNum(metrics.r_squared)} sub="variance explained by index" />
          <Stat label="Info Ratio" info="information_ratio" value={fmtNum(metrics.information_ratio)} />
          <Stat
            label="Up Capture"
            info="up_capture_pct"
            value={metrics.up_capture_pct != null ? `${fmtNum(metrics.up_capture_pct, 0)}%` : '—'}
          />
          <Stat
            label="Down Capture"
            info="down_capture_pct"
            value={metrics.down_capture_pct != null ? `${fmtNum(metrics.down_capture_pct, 0)}%` : '—'}
            sub="lower is better"
          />
        </>}
      </div>

      {hasRegression && (
        <div className="flex flex-col gap-1 border-t border-border/50 pt-2">
          {metrics.alpha_significant === false && (
            <p className="text-[11px] text-muted-foreground">
              Alpha is not statistically distinguishable from zero (|t| &lt; 2). On this sample the
              strategy&apos;s excess return over the index is consistent with chance.
            </p>
          )}
          {metrics.beta > 1.1 && (
            <p className="text-[11px] text-muted-foreground">
              Beta above 1 means the strategy amplifies index moves — part of the return is
              leverage on market direction rather than selection.
            </p>
          )}
          <p className="text-[11px] text-muted-foreground">
            Measured against ^NSEI, a price index: it excludes dividends, so alpha here is
            overstated by roughly the Nifty&apos;s dividend yield (~1–1.5% a year).
          </p>
        </div>
      )}
    </div>
  );
}
